// CDN base URL, or '' to load locally. Validated; no trailing slash.
export function cdnBase(): string {
  const raw = (import.meta.env.VITE_CDN_URL ?? '').trim();
  if (!/^https?:\/\//i.test(raw)) return '';
  return raw.replace(/\/+$/, '');
}

// Full CDN URL for an asset path, or the local path when off.
export function cdnAsset(path: string): string {
  const base = cdnBase();
  return base ? `${base}/${path.replace(/^\/+/, '')}` : path;
}

// Map a CDN URL back to its local URL (used as the fallback).
export function localFromCdn(url: string): string {
  const base = cdnBase();
  return base && url.startsWith(base)
    ? window.location.origin + url.slice(base.length)
    : url;
}

// Load a URL; on failure, try the local copy once.
export async function withLocalFallback<T>(
  url: string,
  load: (_url: string) => Promise<T>
): Promise<T> {
  try {
    return await load(url);
  } catch (err) {
    const local = localFromCdn(url);
    if (local === url) throw err;
    return load(local);
  }
}

// Load many assets from the CDN, else local; look up by original src.
export async function loadBatchWithLocalFallback<T>(
  srcs: string[],
  load: (_urls: string[]) => Promise<Record<string, T>>
): Promise<(_src: string) => T | undefined> {
  try {
    const res = await load(srcs.map(cdnAsset));
    return (src) => res[cdnAsset(src)];
  } catch {
    const res = await load(srcs);
    return (src) => res[src];
  }
}

// Open the CDN connection early so the first asset loads faster. No-op when off.
export function preconnectCdn(): void {
  const base = cdnBase();
  if (!base || typeof document === 'undefined') return;
  let origin: string;
  try {
    origin = new URL(base).origin;
  } catch {
    return;
  }
  if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

// Minimal Phaser loader shape (avoids a Phaser dependency here).
interface CdnLoader {
  setBaseURL(_url: string): unknown;
  start(): unknown;
  on(
    _event: 'loaderror',
    _cb: (_file: { src: string; key: string; type: string }) => void
  ): unknown;
}

// Point a Phaser loader at the CDN; retry failed files locally, else onError.
export function setupPhaserCdn(loader: CdnLoader, onError?: () => void): void {
  const base = cdnBase();
  if (base) {
    loader.setBaseURL(base);
  }
  const retried = new Set<string>();
  loader.on('loaderror', (file) => {
    if (base) {
      const local = localFromCdn(file.src);
      if (local !== file.src && !retried.has(file.key)) {
        retried.add(file.key);
        const add = (loader as unknown as Record<string, unknown>)[file.type];
        if (typeof add === 'function') {
          (add as (_k: string, _u: string) => void).call(
            loader,
            file.key,
            local
          );
          loader.start();
          return;
        }
      }
    }
    onError?.();
  });
}
